if (!customElements.get('pb-hulk-form')) {
  class PbHulkForm extends HTMLElement {
    connectedCallback() {
      this.mount = this.querySelector('.pb-hulk-form__mount');
      this.formId = this.resolveFormId();
      if (!this.mount || !this.formId) return;

      this.mount.id = this.formId;
      this.mount.classList.add('pxFormGenerator');
      this.typography = this.buildTypographySettings();
      this.applyTypographyVariables();
      this.iframeSettings = this.buildIframeSettings();
      this.handleHulkMessage = this.handleHulkMessage.bind(this);
      this.applyInlineOverrides = this.applyInlineOverrides.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleChange = this.handleChange.bind(this);
      window.addEventListener('message', this.handleHulkMessage, true);
      this.mount.addEventListener('focusin', this.handleFocusIn);
      this.mount.addEventListener('focusout', this.handleFocusOut);
      this.mount.addEventListener('change', this.handleChange);
      this.observer = new MutationObserver(this.applyInlineOverrides);
      this.observer.observe(this.mount, { childList: true, subtree: true });
      this.loadForm();
      this.applyInlineOverrides();
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.handleHulkMessage, true);
      this.mount?.removeEventListener('focusin', this.handleFocusIn);
      this.mount?.removeEventListener('focusout', this.handleFocusOut);
      this.mount?.removeEventListener('change', this.handleChange);
      this.observer?.disconnect();
      if (this.retryTimer) window.clearTimeout(this.retryTimer);
    }

    resolveFormId() {
      const directId = (this.dataset.formId || '').trim().replace(/^form_/, '');
      if (directId) return directId;

      const embedCode = this.dataset.embedCode || '';
      const idMatch = embedCode.match(/\bid\s*=\s*["']([^"']+)["']/i);
      if (idMatch?.[1]) return idMatch[1].trim().replace(/^form_/, '');

      const formMatch = embedCode.match(/(?:form_uuid|form-id|form_id)\s*=\s*["']([^"']+)["']/i);
      return formMatch?.[1]?.trim().replace(/^form_/, '') || '';
    }

    loadForm(attempt = 0) {
      if (typeof window.shouldLoadPopup === 'function') {
        window.shouldLoadPopup(this.formId);
        return;
      }

      if (typeof window.RenderFormById === 'function') {
        window.RenderFormById(this.formId);
        return;
      }

      if (attempt >= 40) return;
      this.retryTimer = window.setTimeout(() => this.loadForm(attempt + 1), 150);
    }

    handleHulkMessage(event) {
      if (event.origin !== 'https://formbuilder.hulkapps.com') return;
      if (!event.data || event.data.formid !== this.formId) return;
      window.FbThemeAppExtSettingsHash = this.iframeSettings;
    }

    handleFocusIn(event) {
      if (!event.target.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]), select, textarea, .form-control')) return;
      event.target.style.setProperty('border-color', this.dataset.inputFocusBorderColor, 'important');
      event.target.style.setProperty('background-color', this.dataset.inputFocusBg, 'important');
    }

    handleFocusOut(event) {
      if (!event.target.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]), select, textarea, .form-control')) return;
      event.target.style.setProperty('border-color', this.dataset.inputBorderColor, 'important');
      event.target.style.setProperty('background-color', this.dataset.inputBg, 'important');
    }

    handleChange(event) {
      if (!event.target.matches('select')) return;
      this.applySelectTextColor(event.target);
    }

    applySelectTextColor(select) {
      const color = select.value === '' ? this.dataset.placeholderColor : this.dataset.inputColor;
      select.style.setProperty('color', color, 'important');
    }

    buildIframeSettings() {
      return {
        form_settings: true,
        font_color: this.dataset.labelColor,
        size: this.numberValue('labelSize'),
        input_background_color: this.dataset.inputBg,
        input_background_color_focus: this.dataset.inputFocusBg,
        input_border_radius: this.numberValue('inputRadius'),
        input_border_color: this.dataset.inputBorderColor,
        input_border_color_focus: this.dataset.inputFocusBorderColor,
        input_font_color: this.dataset.inputColor,
        input_size: this.numberValue('inputSize'),
        border_color: this.dataset.formBorderColor,
        border_radius: this.numberValue('formRadius'),
        border: this.numberValue('formBorderWidth'),
        fill_color: this.dataset.formBg,
        padding: this.numberValue('formPadding'),
        form_width: this.numberValue('formWidth'),
        sub_btn_txt_color: this.dataset.buttonColor,
        sub_btn_bg_color: this.dataset.buttonBg,
        sub_btn_font_size: this.numberValue('buttonSize'),
        sub_btn_bdr_color: this.dataset.buttonBorderColor,
        sub_btn_bdr_rad: this.numberValue('buttonRadius'),
        sub_btn_bdr_width: this.numberValue('buttonBorderWidth'),
        fonts: this.dataset.fontFamily
      };
    }

    buildTypographySettings() {
      const custom = this.querySelector('[data-pb-hulk-typography][data-mode="custom"]');
      const keys = [
        'optionColor', 'optionSize', 'optionWeight', 'optionLineHeight',
        'titleColor', 'titleSize', 'titleWeight', 'titleLineHeight',
        'titleMarginTop', 'titleMarginBottom',
        'h3Color', 'h3Size', 'h3Weight', 'h3LineHeight',
        'h3MarginTop', 'h3MarginBottom',
        'h4Color', 'h4Size', 'h4Weight', 'h4LineHeight',
        'h4MarginTop', 'h4MarginBottom',
        'textColor', 'textSize', 'textWeight', 'textLineHeight',
        'textMarginTop', 'textMarginBottom',
        'infoColor', 'infoSize', 'infoWeight', 'infoLineHeight',
        'infoMarginTop', 'infoMarginBottom'
      ];

      return keys.reduce((settings, key) => {
        settings[key] = custom?.dataset[key] || this.dataset[key] || '';
        return settings;
      }, {});
    }

    applyTypographyVariables() {
      const variableMap = {
        optionColor: '--pb-hulk-option-color',
        optionSize: '--pb-hulk-option-size',
        optionWeight: '--pb-hulk-option-weight',
        optionLineHeight: '--pb-hulk-option-line-height',
        titleColor: '--pb-hulk-title-color',
        titleSize: '--pb-hulk-title-size',
        titleWeight: '--pb-hulk-title-weight',
        titleLineHeight: '--pb-hulk-title-line-height',
        titleMarginTop: '--pb-hulk-title-margin-top',
        titleMarginBottom: '--pb-hulk-title-margin-bottom',
        h3Color: '--pb-hulk-h3-color',
        h3Size: '--pb-hulk-h3-size',
        h3Weight: '--pb-hulk-h3-weight',
        h3LineHeight: '--pb-hulk-h3-line-height',
        h3MarginTop: '--pb-hulk-h3-margin-top',
        h3MarginBottom: '--pb-hulk-h3-margin-bottom',
        h4Color: '--pb-hulk-h4-color',
        h4Size: '--pb-hulk-h4-size',
        h4Weight: '--pb-hulk-h4-weight',
        h4LineHeight: '--pb-hulk-h4-line-height',
        h4MarginTop: '--pb-hulk-h4-margin-top',
        h4MarginBottom: '--pb-hulk-h4-margin-bottom',
        textColor: '--pb-hulk-text-color',
        textSize: '--pb-hulk-text-size',
        textWeight: '--pb-hulk-text-weight',
        textLineHeight: '--pb-hulk-text-line-height',
        textMarginTop: '--pb-hulk-text-margin-top',
        textMarginBottom: '--pb-hulk-text-margin-bottom',
        infoColor: '--pb-hulk-info-color',
        infoSize: '--pb-hulk-info-size',
        infoWeight: '--pb-hulk-info-weight',
        infoLineHeight: '--pb-hulk-info-line-height',
        infoMarginTop: '--pb-hulk-info-margin-top',
        infoMarginBottom: '--pb-hulk-info-margin-bottom'
      };

      Object.entries(variableMap).forEach(([key, variable]) => {
        const value = this.typography[key];
        if (!value) return;
        const suffix = key.endsWith('Size') || key.includes('Margin') ? 'px' : '';
        this.style.setProperty(variable, `${value}${suffix}`);
      });
    }

    applyInlineOverrides() {
      const important = (element, property, value) => {
        if (value !== undefined && value !== '') element.style.setProperty(property, value, 'important');
      };
      const family = this.dataset.fontFamily || 'var(--body-font-family)';
      const typography = this.typography || this.buildTypographySettings();

      this.mount.querySelectorAll('.pxFormGenerator, .formContainer, .form_generater_form_div, #formGeneratorForm').forEach((element) => {
        important(element, 'box-sizing', 'border-box');
        important(element, 'width', '100%');
        important(element, 'max-width', '100%');
        important(element, 'font-family', family);
        important(element, 'letter-spacing', '0');
      });

      this.mount.querySelectorAll('.form_generater_form_div').forEach((element) => {
        important(element, 'padding', '0');
        important(element, 'border', '0');
        important(element, 'border-radius', '0');
        important(element, 'background', 'transparent');
        important(element, 'box-shadow', 'none');
      });

      this.mount.querySelectorAll('label, .control-label, .form-label').forEach((element) => {
        important(element, 'color', this.dataset.labelColor);
        important(element, 'font-family', family);
        important(element, 'font-size', `${this.numberValue('labelSize')}px`);
        important(element, 'font-weight', this.dataset.labelWeight);
        important(element, 'line-height', this.dataset.labelLineHeight);
        important(element, 'letter-spacing', '0');
      });

      this.mount.querySelectorAll('.form-group.radio > .radio-list > legend.block_label, .form-group.checkbox-multi > label.block_label, .form-group.checkbox > label.block_label').forEach((element) => {
        element.classList.add('pb-hulk-field-label');
        important(element, 'color', this.dataset.labelColor);
        important(element, 'font-family', family);
        important(element, 'font-size', `${this.numberValue('labelSize')}px`);
        important(element, 'font-weight', this.dataset.labelWeight);
        important(element, 'line-height', this.dataset.labelLineHeight);
        important(element, 'letter-spacing', '0');
      });

      this.mount.querySelectorAll('.form-group.radio > .radio-list > legend.pb-hulk-field-label, .form-group.checkbox-multi > label.pb-hulk-field-label, .form-group.checkbox > label.pb-hulk-field-label').forEach((element) => {
        important(element, 'margin-bottom', '8px');
      });

      this.mount.querySelectorAll('.radio-inline > label, .checkbox-inline > label').forEach((element) => {
        important(element, 'color', typography.optionColor);
        important(element, 'font-family', family);
        important(element, 'font-size', `${Number.parseFloat(typography.optionSize) || this.numberValue('optionSize')}px`);
        important(element, 'font-weight', typography.optionWeight);
        important(element, 'line-height', typography.optionLineHeight);
        important(element, 'letter-spacing', '0');
      });

      const applyTypography = (selector, prefix) => {
        this.mount.querySelectorAll(selector).forEach((element) => {
          important(element, 'color', typography[`${prefix}Color`]);
          important(element, 'font-family', family);
          important(element, 'font-size', `${typography[`${prefix}Size`]}px`);
          important(element, 'font-weight', typography[`${prefix}Weight`]);
          important(element, 'line-height', typography[`${prefix}LineHeight`]);
          important(element, 'letter-spacing', '0');
        });
      };

      applyTypography('.form_title_div, .form_title_div *', 'title');
      applyTypography('.form-group.headings h3, .form-group.headings h3 *', 'h3');
      applyTypography('.form-group.headings h4, .form-group.headings h4 *', 'h4');
      applyTypography('.form-group.paragraph, .form-group.paragraph *, .form_description_div, .form_description_div *', 'text');
      applyTypography('.infoMessage, .infoMessage *', 'info');

      const applySpacing = (selector, prefix) => {
        this.mount.querySelectorAll(selector).forEach((element) => {
          const marginTop = typography[`${prefix}MarginTop`];
          const marginBottom = typography[`${prefix}MarginBottom`];
          if (marginTop !== '') important(element, 'margin-top', `${marginTop}px`);
          if (marginBottom !== '') important(element, 'margin-bottom', `${marginBottom}px`);
        });
      };

      this.mount.querySelectorAll('input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]), select, textarea, .form-control').forEach((element) => {
        important(element, 'box-sizing', 'border-box');
        important(element, 'width', '100%');
        important(element, 'min-height', `${this.numberValue('inputHeight')}px`);
        important(element, 'border', `${this.numberValue('inputBorderWidth')}px solid ${this.dataset.inputBorderColor}`);
        important(element, 'border-radius', `${this.numberValue('inputRadius')}px`);
        important(element, 'background-color', this.dataset.inputBg);
        important(element, 'box-shadow', 'none');
        important(element, 'color', this.dataset.inputColor);
        important(element, 'font-family', family);
        important(element, 'font-size', `${this.numberValue('inputSize')}px`);
        important(element, 'letter-spacing', '0');
      });

      this.mount.querySelectorAll('textarea').forEach((element) => {
        important(element, 'height', `${this.numberValue('textareaHeight')}px`);
        important(element, 'min-height', `${this.numberValue('textareaHeight')}px`);
      });

      this.mount.querySelectorAll('select').forEach((element) => {
        this.applySelectTextColor(element);
      });

      this.mount.querySelectorAll('.form_container > .form-group, #formGeneratorForm > .form-group, #formGeneratorForm > .form-row, #formGeneratorForm > .form-element, #formGeneratorForm > .form-field').forEach((element) => {
        important(element, 'margin-bottom', `${this.numberValue('fieldGap')}px`);
      });

      this.mount.querySelectorAll('.form_container > .form-group .form-group').forEach((element) => {
        important(element, 'margin-bottom', '0');
      });

      this.mount.querySelectorAll('.form_title_div').forEach((element) => {
        important(element, 'display', 'block');
        important(element, 'margin-top', '0');
        important(element, 'margin-bottom', '0');
        important(element, 'padding-top', `${typography.titleMarginTop}px`);
        important(element, 'padding-bottom', `${typography.titleMarginBottom}px`);
        element.querySelectorAll('h1, h2, h3, h4, h5, h6, p').forEach((heading) => {
          important(heading, 'margin-top', '0');
          important(heading, 'margin-bottom', '0');
        });
      });
      this.mount.querySelectorAll('.form-group.headings').forEach((element) => {
        const prefix = element.querySelector('h4') ? 'h4' : 'h3';
        const marginTop = typography[`${prefix}MarginTop`];
        const marginBottom = typography[`${prefix}MarginBottom`];
        if (marginTop !== '') important(element, 'margin-top', `${marginTop}px`);
        if (marginBottom !== '') important(element, 'margin-bottom', `${marginBottom}px`);
        element.querySelectorAll('h3, h4').forEach((heading) => {
          important(heading, 'margin-top', '0');
          important(heading, 'margin-bottom', '0');
        });
      });
      applySpacing('.form-group.paragraph, .form_description_div', 'text');
      applySpacing('.infoMessage', 'info');

      this.mount.querySelectorAll('.form_container').forEach((element) => {
        const halfGap = this.numberValue('columnGap') / 2;
        important(element, 'margin-left', `${-halfGap}px`);
        important(element, 'margin-right', `${-halfGap}px`);
        important(element, 'width', `calc(100% + ${this.numberValue('columnGap')}px)`);
      });

      this.mount.querySelectorAll('.form_container > [class*="col-"]').forEach((element) => {
        const halfGap = this.numberValue('columnGap') / 2;
        important(element, 'padding-left', `${halfGap}px`);
        important(element, 'padding-right', `${halfGap}px`);
      });

      this.mount.querySelectorAll('.terms_conditions .checkbox-inline, .terms_conditions .checkbox-inline > label, .terms_conditions .checkbox_a_link').forEach((element) => {
        important(element, 'font-size', '14px');
        important(element, 'font-weight', '400');
        important(element, 'line-height', '1.5');
      });

      this.mount.querySelectorAll('.terms_conditions .checkbox_a_link').forEach((element) => {
        important(element, 'color', '#de2241');
        important(element, 'text-decoration', 'none');
      });

      this.mount.querySelectorAll('button[type="submit"], input[type="submit"], .btn[type="submit"], .submit-btn').forEach((element) => {
        const wrapper = element.parentElement;
        if (wrapper) {
          const justify = { left: 'flex-start', center: 'center', right: 'flex-end' }[this.dataset.buttonAlign] || 'flex-start';
          wrapper.classList.add('pb-hulk-form__submit-wrap');
          important(wrapper, 'display', 'flex');
          important(wrapper, 'justify-content', justify);
          important(wrapper, 'width', '100%');
        }
        important(element, 'width', this.dataset.buttonWidth === 'full' ? '100%' : 'auto');
        important(element, 'min-height', `${this.numberValue('inputHeight')}px`);
        important(element, 'padding', this.numberValue('buttonPaddingY') + 'px ' + this.numberValue('buttonPaddingX') + 'px');
        important(element, 'border', `${this.numberValue('buttonBorderWidth')}px solid ${this.dataset.buttonBorderColor}`);
        important(element, 'border-radius', `${this.numberValue('buttonRadius')}px`);
        important(element, 'background', this.dataset.buttonBg);
        important(element, 'color', this.dataset.buttonColor);
        important(element, 'font-family', family);
        important(element, 'font-size', `${this.numberValue('buttonSize')}px`);
        important(element, 'font-weight', this.dataset.buttonWeight);
        important(element, 'letter-spacing', '0');
        important(element, 'white-space', 'nowrap');
      });
    }

    numberValue(key) {
      const value = Number.parseFloat(this.dataset[key]);
      return Number.isFinite(value) ? value : 0;
    }
  }

  customElements.define('pb-hulk-form', PbHulkForm);
}
